import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Base } from "../../common/base";
import { getWorkShift, WorkShift } from "../../../models/workShift/workShift";
import {
    getCurrentWorkShift,
    getWorkShifts,
} from "../../../services/workShiftService";
import { setCurrentWorkShiftTimeSlotId, workShiftTimeSlotsReceived } from "./workShiftTimeSlotSlice";

export interface WorkShiftState {
    items: WorkShift[];
    current: WorkShift;
}

const initialState: WorkShiftState = {
    items: [],
    current: null,
};

export const workShiftSlice = createSlice({
    name: "workShift",
    initialState,
    reducers: {
        workShiftsReceived: (
            state,
            action: PayloadAction<{
                workShifts: WorkShift[];
                startDate: string;
                endDate: string;
            }>
        ) => {
            // Preserve existing items outside of given range
            state.items = state.items.filter(
                (h) =>
                    h.effectiveDate < action.payload.startDate ||
                    h.effectiveDate > action.payload.endDate
            );
            state.items = [...state.items, ...action.payload.workShifts];
        },
        setCurrentWorkShift: (state, action: PayloadAction<WorkShift>) => {
            state.current = action.payload;
        },
    },
});

export const { workShiftsReceived, setCurrentWorkShift } =
    workShiftSlice.actions;

export const fetchWorkShifts = createAsyncThunk(
    "workShift/fetch",
    async(
        { startDate, endDate }: { startDate: string; endDate: string },
        thunkApi
    ) => {
        const dtos = await getWorkShifts(
            startDate,
            endDate,
            true,
            thunkApi.signal
        );

        const workShifts = dtos?.map(getWorkShift);

        // Update WorkShiftTimeSlots
        thunkApi.dispatch(
            workShiftTimeSlotsReceived({
                items: dtos.flatMap((d) => d.workShiftTimeSlots),
            })
        );

        thunkApi.dispatch(
            workShiftsReceived({ workShifts, startDate, endDate })
        );
    }
);

export const fetchCurrentWorkShift = createAsyncThunk(
    "workShift/current",
    async(_, thunkApi) => {
        const current = await getCurrentWorkShift(true, thunkApi.signal);

        // Update WorkShiftTimeSlots
        if (current?.workShiftTimeSlots?.length > 0) {
            thunkApi.dispatch(
                workShiftTimeSlotsReceived({
                    items: current.workShiftTimeSlots,
                })
            );
        }

        thunkApi.dispatch(
            setCurrentWorkShiftTimeSlotId(
                current?.workShiftTimeSlots?.find((s) => !s.endDate)?.id
            )
        );
        thunkApi.dispatch(
            setCurrentWorkShift(current ? getWorkShift(current) : null)
        );
    }
);

export const workShiftReducer = workShiftSlice.reducer;

export const makeSelectWorkShiftsByDate = () =>
    createSelector(
        (state: WorkShiftState) => state.items,
        (_, rangeStart: string) => rangeStart,
        (_, _rangeStart, rangeEnd: string) => rangeEnd,
        (items: WorkShift[], rangeStart: string, rangeEnd: string) => {
            if (rangeStart && rangeEnd) {
                return items.filter((i) =>
                    Base.isBetween(
                        i.effectiveDate,
                        rangeStart,
                        rangeEnd,
                        "day",
                        "[]"
                    )
                );
            }
        }
    );
